import { createSlice } from "@reduxjs/toolkit";

import {
  addAisVesselToFleetThunk,
  createMysteryShipThunk,
  deleteMysteryShipThunk,
  fetchAisVessels,
  fetchMysteryShips,
  updateMysteryShipThunk,
} from "../thunks/aisVesselsThunks";

import type { AisVessel, MysteryShip } from "../../utils/types/aisVessels";

interface AisVesselsState {
  aisVessels: AisVessel[];
  mysteryShips: MysteryShip[];
  loading: boolean;
  error: string | null;
  isDataFetched: boolean;
  mysteryShipsFetched: boolean;
  addingToFleet: string | null; // ais_vessel_id currently being added
}

const initialState: AisVesselsState = {
  aisVessels: [],
  mysteryShips: [],
  loading: false,
  error: null,
  isDataFetched: false,
  mysteryShipsFetched: false,
  addingToFleet: null,
};

export const aisVesselsSlice = createSlice({
  name: "aisVessels",
  initialState,
  reducers: {
    resetAisDataFetched: (state) => {
      state.isDataFetched = false;
      state.mysteryShipsFetched = false;
    },
  },
  extraReducers: (builder) => {
    // ── Fetch AIS vessels ──
    builder
      .addCase(fetchAisVessels.pending, (state) => {
        state.loading = true;
        state.isDataFetched = true;
      })
      .addCase(fetchAisVessels.fulfilled, (state, action) => {
        state.aisVessels = action.payload || [];
        state.loading = false;
      })
      .addCase(fetchAisVessels.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isDataFetched = false;
        state.loading = false;
      });

    // ── Add AIS vessel to fleet ──
    builder
      .addCase(addAisVesselToFleetThunk.pending, (state, action) => {
        state.addingToFleet = action.meta.arg.aisVesselId;
      })
      .addCase(addAisVesselToFleetThunk.fulfilled, (state) => {
        state.addingToFleet = null;
      })
      .addCase(addAisVesselToFleetThunk.rejected, (state) => {
        state.addingToFleet = null;
        state.error = "Failed to add vessel to fleet";
      });

    // ── Fetch mystery ships ──
    builder
      .addCase(fetchMysteryShips.pending, (state) => {
        state.mysteryShipsFetched = true;
      })
      .addCase(fetchMysteryShips.fulfilled, (state, action) => {
        state.mysteryShips = action.payload || [];
      })
      .addCase(fetchMysteryShips.rejected, (state, action) => {
        state.error = action.payload as string;
        state.mysteryShipsFetched = false;
      });

    // ── Create mystery ship ──
    builder
      .addCase(createMysteryShipThunk.fulfilled, (state, action) => {
        state.mysteryShips = [action.payload, ...state.mysteryShips];
      });

    // ── Delete mystery ship ──
    builder
      .addCase(deleteMysteryShipThunk.fulfilled, (state, action) => {
        state.mysteryShips = state.mysteryShips.filter(
          (s) => s.mystery_ship_id !== action.payload
        );
      });

    // ── Update mystery ship ──
    builder
      .addCase(updateMysteryShipThunk.fulfilled, (state, action) => {
        const idx = state.mysteryShips.findIndex(
          (s) => s.mystery_ship_id === action.payload.mystery_ship_id
        );
        if (idx >= 0) {
          state.mysteryShips[idx] = { ...state.mysteryShips[idx], ...action.payload };
        }
      });
  },
});

export const { resetAisDataFetched } = aisVesselsSlice.actions;
export default aisVesselsSlice.reducer;
